<template>
  <button
    :type="type"
    class="glass-btn"
    :class="[
      `glass-btn--${variant}`,
      `glass-btn--${size}`,
      { 
        'glass-btn--block': block,
        'glass-btn--loading': loading,
        'glass-btn--icon-only': iconOnly
      }
    ]"
    :disabled="disabled || loading"
    :data-testid="testId"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="glass-btn__loader">
      <svg class="spinner" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="none" stroke-width="3"></circle>
      </svg>
    </span>
    <span v-if="icon && !loading" class="glass-btn__icon glass-btn__icon--left">
      <slot name="icon">
        <i :class="icon"></i>
      </slot>
    </span>
    <span v-if="!iconOnly" class="glass-btn__text">
      <slot></slot>
    </span>
    <span v-if="iconRight && !loading" class="glass-btn__icon glass-btn__icon--right">
      <slot name="icon-right">
        <i :class="iconRight"></i>
      </slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'GlassButton',
  emits: ['click'],
  props: {
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'success', 'danger', 'warning', 'outline', 'ghost'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    testId: {
      type: String,
      default: null
    }
  }
};
</script>

<style scoped>
.glass-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: inherit;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.glass-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Sizes */
.glass-btn--sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: 8px;
}

.glass-btn--md {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 12px;
}

.glass-btn--lg {
  padding: 1rem 2rem;
  font-size: 1.125rem;
  border-radius: 14px;
}

/* Block */
.glass-btn--block {
  width: 100%;
}

/* Icon Only */
.glass-btn--icon-only.glass-btn--sm {
  padding: 0.5rem;
  width: 32px;
  height: 32px;
}

.glass-btn--icon-only.glass-btn--md {
  padding: 0.75rem;
  width: 44px;
  height: 44px;
}

.glass-btn--icon-only.glass-btn--lg {
  padding: 1rem;
  width: 56px;
  height: 56px;
}

/* Variants */
.glass-btn--primary {
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb, 89, 169, 106), 0.9), rgba(var(--theme-primary-rgb, 89, 169, 106), 0.7));
  color: white;
  box-shadow: 0 4px 15px rgba(var(--theme-primary-rgb, 89, 169, 106), 0.3);
}

.glass-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb, 89, 169, 106), 1), rgba(var(--theme-primary-rgb, 89, 169, 106), 0.85));
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--theme-primary-rgb, 89, 169, 106), 0.4);
}

.glass-btn--secondary {
  background: linear-gradient(135deg, rgba(66, 66, 66, 0.9), rgba(66, 66, 66, 0.7));
  color: white;
  box-shadow: 0 4px 15px rgba(66, 66, 66, 0.3);
}

.glass-btn--secondary:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(66, 66, 66, 1), rgba(66, 66, 66, 0.85));
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(66, 66, 66, 0.4);
}

.glass-btn--success {
  background: linear-gradient(135deg, rgba(89, 169, 106, 0.9), rgba(89, 169, 106, 0.7));
  color: white;
  box-shadow: 0 4px 15px rgba(89, 169, 106, 0.3);
}

.glass-btn--success:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(89, 169, 106, 1), rgba(89, 169, 106, 0.85));
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(89, 169, 106, 0.4);
}

.glass-btn--danger {
  background: linear-gradient(135deg, rgba(195, 66, 63, 0.9), rgba(195, 66, 63, 0.7));
  color: white;
  box-shadow: 0 4px 15px rgba(195, 66, 63, 0.3);
}

.glass-btn--danger:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(195, 66, 63, 1), rgba(195, 66, 63, 0.85));
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(195, 66, 63, 0.4);
}

.glass-btn--warning {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7));
  color: #424242;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
}

.glass-btn--warning:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(255, 193, 7, 1), rgba(255, 193, 7, 0.85));
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
}

.glass-btn--outline {
  background: transparent;
  color: var(--theme-primary, #59a96a);
  border: 2px solid var(--theme-border-color, rgba(89, 169, 106, 0.5));
}

.glass-btn--outline:hover:not(:disabled) {
  background: var(--theme-hover-bg, rgba(89, 169, 106, 0.1));
  border-color: var(--theme-primary, rgba(89, 169, 106, 0.8));
  transform: translateY(-2px);
}

.glass-btn--ghost {
  background: transparent;
  color: #424242;
}

.glass-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
}

/* Loading */
.glass-btn--loading .glass-btn__text {
  opacity: 0.7;
}

.glass-btn__loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.glass-btn__loader .spinner {
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

.glass-btn__loader .spinner circle {
  stroke: currentColor;
  stroke-dasharray: 60;
  stroke-dashoffset: 45;
  stroke-linecap: round;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

/* Icons */
.glass-btn__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
}
</style>
